import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface BreadcrumbItem {
  label: React.ReactNode;
  /** Кликабельный bread: либо колбэк, либо роут для navigate(). Если оба пусты —
   * элемент рендерится как нерасклинкаемый (как и последний/current-элемент). */
  onClick?: () => void;
  to?: string;
  /** Опциональная иконка слева от лейбла (например IconArrowLeft в editor-баре). */
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  /** Список крошек. Последний элемент всегда отрисуется как «current» (с акцентом
   * по цвету и веса 500, без клика). */
  items: BreadcrumbItem[];
  /** Кастомный разделитель. По умолчанию `/`. */
  separator?: React.ReactNode;
  /** Шрифтовой размер. `md` (13px) — для page-bar (CourseEditor). `sm` (12px) — для
   * inline-крошки внутри статьи (docs). */
  size?: 'sm' | 'md';
  className?: string;
  /** Стиль на корневой контейнер. Используется для переопределения CSS-переменных,
   * например на странице со скоупом другой палитры (`/docs`). */
  style?: React.CSSProperties;
}

// Стили инжектятся один раз на модуль. Так компонент остаётся self-contained
// (не нужен импорт CSS-файла) и при этом мы не плодим <style> на каждый рендер.
const STYLE_ID = 'app-breadcrumbs-styles';
let stylesInjected = false;
function ensureStyles(): void {
  if (stylesInjected || typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) {
    stylesInjected = true;
    return;
  }
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    .app-breadcrumbs {
      --bc-muted: var(--mantine-color-dimmed);
      --bc-current: var(--mantine-color-text);
      --bc-soft: var(--mantine-color-placeholder);
      display: flex; align-items: center; gap: 8px; min-width: 0;
      font-family: inherit;
    }
    .app-breadcrumbs-sm { font-size: 12px; }
    .app-breadcrumbs-md { font-size: 13px; }
    .app-breadcrumbs-item { color: var(--bc-muted); display: inline-flex; align-items: center; gap: 4px; }
    .app-breadcrumbs-item button {
      background: none; border: none; color: inherit; cursor: pointer; padding: 0;
      font: inherit; display: inline-flex; align-items: center; gap: 4px;
    }
    .app-breadcrumbs-item button:hover { color: var(--bc-current); }
    .app-breadcrumbs-current {
      color: var(--bc-current); font-weight: 500;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      max-width: 360px; display: inline-flex; align-items: center; gap: 4px;
    }
    .app-breadcrumbs-sep { color: var(--bc-soft); user-select: none; }
  `;
  document.head.appendChild(el);
  stylesInjected = true;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  separator = '/',
  size = 'md',
  className,
  style,
}) => {
  ensureStyles();
  const navigate = useNavigate();
  const lastIdx = items.length - 1;

  const handleClick = (item: BreadcrumbItem) => {
    if (item.onClick) item.onClick();
    else if (item.to) navigate(item.to);
  };

  return (
    <div
      className={`app-breadcrumbs app-breadcrumbs-${size}${className ? ` ${className}` : ''}`}
      style={style}
    >
      {items.map((item, i) => {
        const isLast = i === lastIdx;
        const isClickable = !isLast && (item.onClick || item.to);
        return (
          <React.Fragment key={i}>
            {i > 0 && <span className="app-breadcrumbs-sep">{separator}</span>}
            {isLast ? (
              <span className="app-breadcrumbs-current">
                {item.icon}
                {item.label}
              </span>
            ) : isClickable ? (
              <span className="app-breadcrumbs-item">
                <button type="button" onClick={() => handleClick(item)}>
                  {item.icon}
                  {item.label}
                </button>
              </span>
            ) : (
              <span className="app-breadcrumbs-item">
                {item.icon}
                {item.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
